import api from '../utils/api';

export const listFiles = async (storageId, {
  skip = 0,
  limit = 10,
  search = '',
  path = '',
  folder = null,
  contentType = null,
  isPublic = null
} = {}) => {
  try {
    const params = new URLSearchParams();

    // Only append parameters that have values
    if (skip) params.append('skip', skip);
    if (limit) params.append('limit', limit);
    if (search) params.append('search', search);
    if (path) params.append('path', path);
    if (folder !== null) params.append('folder', folder);
    if (contentType) params.append('content_type', contentType);
    if (isPublic !== null) params.append('is_public', isPublic.toString());
    
    const url = `/storage/${storageId}/files${params.toString() ? `?${params}` : ''}`;
    
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error listing files:', error);
    // Provide more detailed error information
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
      
      // If it's a validation error, log the details
      if (error.response.status === 422 && error.response.data && error.response.data.detail) {
        console.error('Validation errors:', error.response.data.detail);
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    throw error;
  }
};

export const uploadFile = async (storageId, file, { path = '', isPublic = false } = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (path) formData.append('path', path);
    formData.append('is_public', isPublic);
    
    const response = await api.post(`/storage/${storageId}/files/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error uploading file:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    throw error;
  }
};

export const downloadFile = async (storageId, fileId) => {
  try {
    const response = await api.get(`/storage/${storageId}/files/${fileId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading file:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    }
    throw error;
  }
};

export const deleteFile = async (storageId, fileId) => {
  try {
    const response = await api.delete(`/storage/${storageId}/files/${fileId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting file:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    throw error;
  }
};

export const previewFile = async (storageId, fileId, { maxBytes = 102400 } = {}) => {
  const params = maxBytes !== 102400 ? `?max_bytes=${maxBytes}` : '';
  const response = await api.get(`/storage/${storageId}/files/${fileId}/preview${params}`);
  return response.data;
};

export const getPresignedUrl = async (storageId, fileId, expiresIn = 3600) => {
  try {
    const response = await api.get(`/storage/${storageId}/files/${fileId}/presigned-url`, {
      params: { expires_in: expiresIn },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    throw error;
  }
};

// List storages
export const listStorages = async ({ skip = 0, limit = 100 } = {}) => {
  const response = await api.get('/storage/', { params: { skip, limit } });
  return response.data;
};

// List folders for a storage
export const listFolders = async (storageId, { parent = '' } = {}) => {
  const params = parent ? `?parent=${encodeURIComponent(parent)}` : '';
  const response = await api.get(`/storage/${storageId}/folders${params}`);
  return response.data;
};

// Create a folder
export const createFolder = async (storageId, path) => {
  const response = await api.post(`/storage/${storageId}/folders`, { path });
  return response.data;
};

// Upload multiple files
export const uploadMultipleFiles = async (storageId, files, { folder = '', isPublic = false } = {}) => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  const params = new URLSearchParams();
  if (folder) params.append('folder', folder);
  if (isPublic) params.append('is_public', 'true');
  const url = `/storage/${storageId}/files/upload-multiple${params.toString() ? `?${params}` : ''}`;
  const response = await api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Download multiple files as zip
export const downloadZip = async (storageId, fileIds) => {
  const response = await api.post(`/storage/${storageId}/files/download-zip`,
    { file_ids: fileIds },
    { responseType: 'blob' }
  );
  return response.data;
};

// Move a file to a different folder
export const moveFile = async (storageId, fileId, newFolder) => {
  const response = await api.put(`/storage/${storageId}/files/${fileId}/move`, { new_folder: newFolder });
  return response.data;
};
